import {
  Box,
  CloseButton,
  Flex,
  useColorModeValue,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';

import { FiHome, FiLayers, FiBookOpen, FiSettings } from 'react-icons/fi';
import { GiProgression } from 'react-icons/gi';
import { FaClipboardList, FaThList } from 'react-icons/fa';
import { HiUserGroup } from 'react-icons/hi';

import { ADMIN, MANAGER, TRAINER } from 'utils/constants/roles';
import NavItem from './NavItem';

function SidebarContent({ onClose, ...rest }) {
  const { accounts } = useMsal();

  // Safe access to user roles with proper null/undefined checks
  const userAccount = accounts?.[0];
  const idTokenClaims = userAccount?.idTokenClaims;
  const roles = idTokenClaims?.roles || [];

  // Check if user has admin or trainer role for conditional routing
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );

  /**
   * Determine the Classes route and role requirements based on user permissions
   * - Users with ADMIN or TRAINER roles: Navigate to /admin/classes with role restrictions
   * - Users without ADMIN or TRAINER roles: Navigate to /classes with no role restrictions
   * This allows regular users to access classes while maintaining admin functionality for privileged users
   */
  const classesConfig = hasAdminOrTrainerRole
    ? {
        to: '/admin/classes',
        roles: [ADMIN, TRAINER],
      }
    : {
        to: '/classes',
        roles: [], // No role restrictions for regular users accessing classes
      };

  const navigationItems = [
    { name: 'HOME', icon: FiHome, to: '/', base: 'home' },
    {
      name: 'COURSES',
      icon: FiLayers,
      to: '/admin/courses',
      base: 'courses',
      roles: [ADMIN, TRAINER],
    },
    {
      // Dynamic Classes navigation: route and permissions determined by user role
      name: 'CLASSES',
      icon: HiUserGroup,
      to: classesConfig.to, // Either '/admin/classes' or '/classes' based on user role
      base: 'classes',
      roles: classesConfig.roles, // Role restrictions applied conditionally
    },

    {
      name: 'LEARNING PLANS',
      icon: FaClipboardList,
      to: '/admin/learning-plans',
      base: 'learning-plans',
      roles: [ADMIN, TRAINER],
    },
    {
      name: 'REPORTS',
      icon: FiSettings,
      base: 'reports',
      roles: [ADMIN, TRAINER, MANAGER],
      items: [
        {
          name: 'COMPLETION',
          icon: GiProgression,
          to: '/admin/reports/completion',
          base: 'completion',
          roles: [ADMIN, TRAINER, MANAGER],
        },
        {
          name: 'SUB-QUESTS',
          icon: FaThList,
          to: '/admin/reports/sub-quests',
          base: 'sub-quests',
          roles: [ADMIN, TRAINER],
        },
      ],
    },
    {
      name: 'CATEGORIES',
      icon: FiBookOpen,
      to: '/admin/categories',
      base: 'categories',
      roles: [ADMIN],
    },
  ];

  return (
    <Box
      bg={useColorModeValue('#d92e48', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: '80px' }}
      pos="absolute"
      minHeight="100%"
      {...rest}
    >
      <Flex justify="end" display={{ base: 'flex', md: 'none' }}>
        <Box p="4">
          <CloseButton
            display={{ base: 'flex', md: 'none' }}
            color="white"
            onClick={onClose}
          />
        </Box>
      </Flex>

      <Grid
        display={{ base: 'grid', md: 'none' }}
        templateColumns="repeat(3, 1fr)"
        gap={6}
      >
        {navigationItems.map((link) => (
          <GridItem w="100%" key={link.name}>
            <NavItem
              to={link.to}
              base={link.base}
              icon={link.icon}
              items={link?.items}
              requiredRoles={link?.roles}
            >
              {link.name}
            </NavItem>
          </GridItem>
        ))}
      </Grid>

      <Box hideBelow="md">
        {navigationItems.map((link) => (
          <NavItem
            key={link.name}
            to={link.to}
            base={link.base}
            icon={link.icon}
            items={link?.items}
            requiredRoles={link?.roles}
          >
            {link.name}
          </NavItem>
        ))}
      </Box>
    </Box>
  );
}

SidebarContent.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default SidebarContent;
